import { ChipProps } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { FieldErrors } from 'react-hook-form';
import {
  EngagementTypeWithRules,
  RuleType,
} from '../components/contract/package/components/engagement-types-rules';
import {
  BillableActivitiesWithDetails,
  ContractInnerPackageFormValues,
  ContractPackageFormValues,
  ContractTemplateOption,
  ContractTypeExtension,
  DEFAULT_FORM_CURRENCY,
  DEFAULT_PACKAGE_FORM_VALUES,
  RawContractPackageFormValues,
  RawInnerPackageFormValues,
  ValidBillingModel,
} from '../components/contract/package/custom-package-types';
import {
  IN_PERSON_NAME_KEY,
  NO_DATA_TEMPLATE,
} from '../constants/strings/contract/form-constants';
import {
  BillableActivityType,
  BillingModelType,
  ClientInsurerType,
  ContractPackagesType,
  ContractTemplateType,
  ContractType,
  GetContractTemplatesQuery,
  PartnershipType,
} from '../types';
import { capitalizeFirstLetterAndSpaceSnakeString } from './bills-utils';
import { formatCurrency, getCurrencySymbol } from './currency-helpers';
import { cleanTypeNameGraphql } from './graphql-utils';

dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);

export enum ActiveStatus {
  Active = 'Active',
  Past = 'Past',
  Future = 'Future',
  Unknown = 'Unknown',
  Void = 'Void',
}

export const calculateChipColor = (
  status?: ActiveStatus,
): ChipProps['color'] => {
  switch (status) {
    case ActiveStatus.Active:
      return 'primary';
    case ActiveStatus.Past:
      return 'default';
    case ActiveStatus.Future:
      return 'secondary';
    default:
      return 'error';
  }
};

export const computeActiveStatus = (
  isVoid: boolean,
  startDate: string | Dayjs | null,
  endDate?: string | Dayjs | null,
): ActiveStatus => {
  if (isVoid) return ActiveStatus.Void;
  const today = dayjs();
  const start = dayjs(startDate);

  if (!endDate) {
    return start.isAfter(today, 'date')
      ? ActiveStatus.Future
      : ActiveStatus.Active;
  }

  const end = dayjs(endDate);

  if (today.isBetween(start, end, 'date', '[]')) {
    return ActiveStatus.Active;
  }

  if (today.isAfter(end, 'date')) {
    return ActiveStatus.Past;
  }

  if (today.isBefore(start, 'date')) {
    return ActiveStatus.Future;
  }
  return ActiveStatus.Unknown;
};

export const findActiveContract = (
  contracts: ContractType[],
): ContractType | undefined =>
  contracts.find(
    contract =>
      computeActiveStatus(
        contract.void,
        contract.startDate,
        contract.endDate,
      ) === ActiveStatus.Active,
  );

export const dateSorterFunction = (
  aStartDate: string,
  bStartDate: string,
): number => {
  const aStart = dayjs(aStartDate);
  const bStart = dayjs(bStartDate);
  if (aStart.isBefore(bStart)) {
    return 1;
  }
  if (aStart.isAfter(bStart)) {
    return -1;
  }
  return 0;
};

export const sortContracts = (
  contracts: ContractType[],
  desc = false,
): ContractType[] => {
  if (desc) {
    return [...contracts].sort((a, b) =>
      dateSorterFunction(b.startDate, a.startDate),
    );
  }
  return [...contracts].sort((a, b) =>
    dateSorterFunction(a.startDate, b.startDate),
  );
};

export const getMilestoneName = (
  milestone: ContractTypeExtension,
): string | null => {
  if ('name' in milestone) {
    const name = milestone.name?.toUpperCase() ?? '';
    if (name === 'C') {
      return 'Standard';
    }
    if (name.startsWith('C')) {
      return `Milestone ${name.substring(1)}`;
    }
  }
  return null;
};

export interface DateOverlapCheckReturn {
  startDateOverlap: boolean;
  endDateOverlap: boolean;
}

export const isDateRangeOverlappingContracts = (
  startDate: Date,
  endDate: Date | null,
  currentContracts: ContractType[],
  contractEditId?: number,
): DateOverlapCheckReturn => {
  let startDateOverlap = false;
  let endDateOverlap = false;

  if (contractEditId) {
    currentContracts = currentContracts.filter(
      contract => contract.id !== contractEditId,
    );
  }
  currentContracts = currentContracts
    .filter(contract => contract.void !== true)
    .sort((a, b) => dayjs(a.startDate).diff(dayjs(b.startDate)));

  const selectedStart = dayjs(startDate);
  const selectedEnd = endDate ? dayjs(endDate) : null;

  for (const contract of currentContracts) {
    const contractStart = dayjs(contract.startDate);
    const contractEnd = contract.endDate ? dayjs(contract.endDate) : null;

    if (selectedStart && selectedEnd) {
      if (
        contractStart.isBetween(selectedStart, selectedEnd, 'day', '[]') &&
        contractEnd?.isBetween(selectedStart, selectedEnd, 'day', '[]')
      ) {
        startDateOverlap = true;
        endDateOverlap = true;
        break;
      }
      startDateOverlap =
        (contractEnd &&
          selectedStart.isBetween(contractStart, contractEnd, 'day', '[]')) ||
        (!contractEnd && selectedStart.isSameOrAfter(contractStart, 'day'));
      endDateOverlap =
        (contractEnd &&
          selectedEnd.isBetween(contractStart, contractEnd, 'day', '[]')) ||
        (!contractEnd && selectedEnd.isSameOrAfter(contractStart, 'day'));
    } else {
      startDateOverlap =
        (contractEnd &&
          selectedStart.isBetween(contractStart, contractEnd, 'day', '[]')) ||
        (!contractEnd && selectedStart.isSameOrAfter(contractStart, 'day'));
      endDateOverlap =
        selectedStart.isSameOrBefore(contractStart, 'day') ||
        (!contractEnd && selectedStart.isSameOrAfter(contractStart, 'day'));
    }

    if (startDateOverlap || endDateOverlap) {
      break;
    }
  }

  return {
    startDateOverlap,
    endDateOverlap,
  };
};

const getNearestContract = (
  date: string,
  contracts: ContractType[],
  prevOrNext: 'prev' | 'next' = 'prev',
): ContractType | null => {
  const selectedDate = dayjs(date);

  return contracts
    .filter(contract => contract.void === false)
    .reduce((next: ContractType | null, contract): ContractType | null => {
      const start = dayjs(contract.startDate);
      if (prevOrNext === 'prev' && start.isBefore(selectedDate)) {
        if (
          !next ||
          selectedDate.diff(dayjs(next?.startDate)) >
            selectedDate.diff(dayjs(contract.startDate))
        ) {
          return contract;
        }
      } else if (prevOrNext === 'next' && start.isAfter(selectedDate)) {
        if (
          !next ||
          dayjs(next.startDate).diff(selectedDate) >
            dayjs(contract.startDate).diff(selectedDate)
        ) {
          return contract;
        }
      }
      return next;
    }, null);
};

export interface GapReturnObject {
  isGap: boolean;
  gapLength: number;
}

export const isDateGap = (
  date: string,
  contractEditId: number | null,
  contracts: ContractType[],
  startOrEnd: 'start' | 'end' = 'start',
): GapReturnObject => {
  let isGap = false;
  let gapLength = 0;

  if (contractEditId)
    contracts = contracts.filter(c => c.id !== contractEditId);

  const nearestContract = getNearestContract(
    date,
    contracts,
    startOrEnd === 'start' ? 'prev' : 'next',
  );

  if (startOrEnd === 'start' && nearestContract && nearestContract.endDate) {
    if (dayjs(date).isAfter(dayjs(nearestContract.endDate).add(1, 'day'))) {
      isGap = true;
      gapLength = dayjs(date).diff(
        dayjs(nearestContract.endDate).add(1, 'day'),
        'day',
      );
    }
  } else if (
    startOrEnd === 'end' &&
    nearestContract &&
    nearestContract.startDate
  ) {
    if (
      dayjs(date).isBefore(dayjs(nearestContract.startDate).subtract(1, 'day'))
    ) {
      isGap = true;
      gapLength = dayjs(nearestContract.startDate).diff(
        dayjs(date).add(1, 'day'),
        'day',
      );
    }
  }
  return {
    isGap,
    gapLength,
  };
};

export const checkContractsForGaps = (contracts: ContractType[]): boolean =>
  sortContracts(
    contracts.filter(c => c.void !== true),
    true,
  ).some((contract, index, sortedContracts) => {
    const nextContract = sortedContracts[index + 1];
    if (nextContract) {
      if (
        !dayjs(contract.endDate)
          .add(1, 'day')
          .isSame(dayjs(nextContract.startDate))
      ) {
        return true;
      }
    }
  });

export const getLastContract = (
  historicContracts: ContractType[],
): ContractType | null => {
  if (historicContracts.length > 0) {
    const sortedContracts = sortContracts(historicContracts);
    return sortedContracts[0];
  } else {
    return null;
  }
};

/**
 * Calculate default calender month for end date
 */
export const computeEndDateDefault = (
  startDate: Dayjs | null,
  currentContracts: ContractType[],
): Dayjs => {
  if (startDate) {
    return dayjs(startDate);
  } else if (getLastContract(currentContracts)?.endDate) {
    return dayjs(getLastContract(currentContracts)?.endDate).add(1, 'day');
  } else {
    return dayjs();
  }
};
/**
 * Calculate default calender month for start date
 */
export const computeStartDateDefault = (
  currentContracts: ContractType[],
): Dayjs => {
  const lastContractEndDate = dayjs(getLastContract(currentContracts)?.endDate);
  if (currentContracts.length > 0 && lastContractEndDate.isValid()) {
    return lastContractEndDate.add(1, 'day');
  } else {
    return dayjs().add(1, 'day');
  }
};
/**
 * Derived state for checking start date is before end
 */
export const startDateBeforeEndCheck = (
  startDate: Dayjs | null,
  endDate: Dayjs | null,
): boolean => {
  if (dayjs(startDate).isValid() && dayjs(endDate).isValid()) {
    return dayjs(startDate).isBefore(dayjs(endDate));
  } else {
    return true;
  }
};

export interface CurrentInPersonVisitData {
  inPersonVisitContractPackageId: number | null;
  enableInPersonVisit: boolean;
  inPersonVisitCharge: number;
  inPersonVisitEffectiveDate: Date | null;
}
/**
 * Pull out in person visit package details
 */
export const currentInPersonVisitData = (
  currentContractPackages: ContractPackagesType[] | undefined,
): CurrentInPersonVisitData => {
  const currentInPersonVisitPackage = extractProductPackages(
    currentContractPackages ?? [],
  ).filter(cp => cp.key === IN_PERSON_NAME_KEY)[0]?.contractPackage;
  if (currentInPersonVisitPackage) {
    return {
      inPersonVisitContractPackageId: currentInPersonVisitPackage.id,
      enableInPersonVisit: true,
      inPersonVisitCharge: currentInPersonVisitPackage.price,
      inPersonVisitEffectiveDate: dayjs(
        currentInPersonVisitPackage.startDate,
      ).toDate(),
    };
  }
  return {
    inPersonVisitContractPackageId: null,
    enableInPersonVisit: false,
    inPersonVisitCharge: 150.0,
    inPersonVisitEffectiveDate: null,
  };
};

export const extractMilestones = (
  data: ContractTypeExtension[],
): { key: string; payment: number; name: string; value: string }[] =>
  data.reduce<{ key: string; payment: number; name: string; value: string }[]>(
    (coll, milestone) => {
      const name = getMilestoneName(milestone);
      if (name && 'payment' in milestone) {
        coll.push({
          key: milestone.name ?? '',
          name,
          value: formatCurrency(milestone?.payment ?? 0),
          payment: milestone?.payment ?? 0,
        });
      }
      return coll;
    },
    [],
  );

export const extractProductPackages = (
  data: ContractPackagesType[],
): { key: string; contractPackage: ContractPackagesType }[] =>
  data.reduce<{ key: string; contractPackage: ContractPackagesType }[]>(
    (coll, contractPackage) => {
      const name = contractPackage.package?.name || null;
      if (name) {
        coll.push({
          key: name,
          contractPackage,
        });
      }
      return coll;
    },
    [],
  );

enum contractType {
  'Milestone',
  'Standard',
  'Other',
}

export const determineContractType = (
  templateName: string,
): string | undefined =>
  Object.keys(contractType).find(key =>
    key.length > 1 ? (RegExp(`${key}`).exec(templateName)?.pop() ?? '') : '',
  );

const mapEngagementTypeRuleToBillableActivitiesWithDetails = (
  engagementTypesWithRules: EngagementTypeWithRules[],
): BillableActivitiesWithDetails =>
  engagementTypesWithRules.reduce((acc: BillableActivitiesWithDetails, ent) => {
    if (ent.defaultSelected) {
      const defaultSubType = ent.subTypeOptions?.[
        RuleType[ent.name as keyof typeof RuleType]
      ]?.find(subType => subType.defaultSelected);
      acc[ent.id] = defaultSubType
        ? {
            [RuleType[ent.name as keyof typeof RuleType]]: defaultSubType.value,
          }
        : null;
    }
    return acc;
  }, {});

const mapContractBillableActivitiesToBillableActivitiesWithDetails = (
  billableActivities: BillableActivityType[],
): BillableActivitiesWithDetails =>
  billableActivities.reduce((acc: BillableActivitiesWithDetails, activity) => {
    acc[activity.engagementTypeId] = activity.details ?? null;
    return acc;
  }, {});

export const getBillableActivitiesFromContractPackageOrDefault = (
  engagementTypes: EngagementTypeWithRules[],
  index: number,
  existingContract?: ContractType,
): BillableActivitiesWithDetails => {
  if (
    existingContract?.packageEnabled &&
    existingContract?.contractPackages?.[index]
  ) {
    const contractPackage = existingContract.contractPackages[index];
    return mapContractBillableActivitiesToBillableActivitiesWithDetails(
      contractPackage.billableActivityTypes,
    );
  }
  return mapEngagementTypeRuleToBillableActivitiesWithDetails(engagementTypes);
};

export const getDefaultPackageStartDate = (
  contractStartDate: Date | null,
): Date => {
  if (contractStartDate) {
    return dayjs(contractStartDate).toDate();
  }
  return dayjs().startOf('day').toDate();
};

export const parsePackageFormValues = (
  data: RawContractPackageFormValues,
  billingModels: BillingModelType[],
): ContractPackageFormValues => ({
  startDate: dayjs(data.startDate).format('YYYY-MM-DD'),
  endDate: dayjs(data.endDate).isValid()
    ? dayjs(data.endDate).format('YYYY-MM-DD')
    : null,
  userAnnualCap: data.userAnnualCap,
  globalDailyEngagementCap: data.globalDailyEngagementCap ?? null,
  currency: data.currency?.value ?? 'USD',
  void: data.isVoid ?? false,
  partnershipId: data.partnershipId,
  billingType: data.billingType ?? null,
  contractPackages: data.selectedPackages?.map(
    (selectedPackage): ContractInnerPackageFormValues => {
      const basePackage: ContractInnerPackageFormValues = {
        id: selectedPackage.id ?? null,
        billingModelId: selectedPackage.billingModelId ?? 0,
        packageId: selectedPackage.packageId ?? 0,
        price: selectedPackage.price,
        billableActivityTypes: selectedPackage.selectedBillableActivities,
        startDate: dayjs(selectedPackage.startDate ?? data.startDate).format(
          'YYYY-MM-DD',
        ),
        billingTypeOverride: selectedPackage.billingTypeOverride ?? null,
        // When editing an existing package (id !== null), explicitly set contractTemplateId to null
        // so the backend clears any stale values from previous billing model configurations.
        // Note: contractTemplateId will be overridden by milestone model branch when appropriate.
        // billingTypeOverride is preserved from the form value above.
        ...(selectedPackage.id !== null ? { contractTemplateId: null } : {}),
      };
      if (isEngagementModelById(billingModels, basePackage.billingModelId)) {
        return {
          ...basePackage,
          rules: {
            activationFee: selectedPackage.initialFee,
            activityFee: selectedPackage.activityFee,
          },
        };
      }
      if (isMilestoneModelById(billingModels, basePackage.billingModelId)) {
        return {
          ...basePackage,
          rules: selectedPackage?.currentTemplate?.contract,
          contractTemplateId:
            selectedPackage?.currentTemplate?.contractTemplateId,
        };
      }
      if (
        isFeeForServiceModelById(billingModels, basePackage.billingModelId) &&
        selectedPackage.costShareRequired &&
        selectedPackage.deductionTiming &&
        selectedPackage.cadence
      ) {
        return {
          ...basePackage,
          costShareOptions: {
            deductionTiming: selectedPackage.deductionTiming,
            cadence: selectedPackage.cadence,
          },
        };
      }
      if (
        isAdminFeeClaimsModelById(billingModels, basePackage.billingModelId)
      ) {
        return {
          ...basePackage,
          rules: {
            feePercent: selectedPackage.feePercent,
          },
          billableActivityTypes: {},
        };
      }

      return basePackage;
    },
  ),
});

export const getClientsInsurerPartnership = (
  data: ClientInsurerType | null,
): PartnershipType | undefined =>
  data?.partnerships?.filter(p => p.partnershipType === 'billing').pop();

export const filterTemplatesByPartnershipsAndBillingModel = (
  allTemplates: ContractTemplateType[],
  partnershipIds?: number[],
  billingModelName?: string,
): ContractTemplateOption[] =>
  allTemplates
    .filter(
      template =>
        !template.partnershipId ||
        (partnershipIds && partnershipIds.includes(template.partnershipId)),
    )
    .filter(
      template =>
        !billingModelName || template.contractType === billingModelName,
    )
    .map(temp => ({
      name: temp.name,
      contractTemplateId: temp.id,
      contract: temp.contract,
    }))
    .toSorted((a, b) => a.name.localeCompare(b.name)) ?? [NO_DATA_TEMPLATE];

export const isMilestoneModelById = (
  billingModels: BillingModelType[],
  billingModelId: number,
): boolean =>
  billingModels
    .filter(model =>
      [
        ValidBillingModel.INDIVIDUAL_MILESTONE,
        ValidBillingModel.COHORT_MILESTONE,
      ].includes(model.name as ValidBillingModel),
    )
    .map(model => model.id)
    .includes(billingModelId);

export const isEngagementModelById = (
  billingModels: BillingModelType[],
  billingModelId: number,
): boolean =>
  billingModels.find(model => model.name === ValidBillingModel.ENGAGEMENT)
    ?.id === billingModelId;

export const isFeeForServiceModelById = (
  billingModels: BillingModelType[],
  billingModelId: number,
): boolean =>
  billingModels.find(model => model.name === ValidBillingModel.FEE_FOR_SERVICE)
    ?.id === billingModelId;

export const isAdminFeeClaimsModelById = (
  billingModels: BillingModelType[],
  billingModelId: number,
): boolean =>
  billingModels.find(model => model.name === ValidBillingModel.ADMIN_FEE_CLAIMS)
    ?.id === billingModelId;

export const isFeeForServicePackageByName = (packageName: string): boolean =>
  ['ptvv', 'in_person_visit'].includes(packageName.toLowerCase());

export const isEngagementModelByName = (billingModelName: string): boolean =>
  billingModelName === ValidBillingModel.ENGAGEMENT;

export const isAdminFeeClaimsModelByName = (
  billingModelName: string,
): boolean => billingModelName === ValidBillingModel.ADMIN_FEE_CLAIMS;

// We don't always have access to whole billing models db objects so we need to check by name
export const isMilestoneModelByName = (billingModelName: string): boolean =>
  [
    ValidBillingModel.INDIVIDUAL_MILESTONE,
    ValidBillingModel.COHORT_MILESTONE,
  ].includes(billingModelName as ValidBillingModel);

export const getContractToEditTemplate = (
  injectedValues: ContractType,
  templates: GetContractTemplatesQuery['getContractTemplates'],
): ContractTemplateOption => ({
  name:
    templates?.find(
      template => template.id === injectedValues?.contractTemplateId,
    )?.name ?? 'No matching template id',
  contractTemplateId: injectedValues.contractTemplateId ?? -1,
  contract: injectedValues?.contract ?? [{}],
});

export const getResetPackageFormValues = (
  billingModels: BillingModelType[],
  templates: ContractTemplateType[],
  contractToEdit?: ContractType,
  partnershipId?: number,
  billingType?: string | null,
): RawContractPackageFormValues => ({
  startDate: contractToEdit?.startDate
    ? dayjs(contractToEdit?.startDate).toDate()
    : null,
  endDate: contractToEdit?.endDate
    ? dayjs(contractToEdit?.endDate).toDate()
    : null,
  userAnnualCap: contractToEdit?.userAnnualCap ?? 1750.0,
  globalDailyEngagementCap: contractToEdit?.globalDailyEngagementCap ?? null,
  currency: contractToEdit?.currency
    ? {
        label: `${contractToEdit.currency} ${getCurrencySymbol(
          contractToEdit.currency,
        )}`,
        value: contractToEdit.currency,
      }
    : DEFAULT_FORM_CURRENCY,
  isVoid: contractToEdit?.void ?? false,
  partnershipId: contractToEdit
    ? (contractToEdit.partnershipId ?? null)
    : (partnershipId ?? null),
  billingType: contractToEdit?.billingType ?? billingType ?? null,
  selectedPackages: contractToEdit?.contractPackages?.some(Boolean)
    ? contractToEdit.contractPackages.map(cp => ({
        id: cp.id,
        billingModelId: cp.billingModelId,
        packageId: cp.package.id,
        price: cp.price,
        initialFee: cp.rules?.activationFee ?? 250,
        activityFee: cp.rules?.activityFee ?? 50,
        feePercent: cp.rules?.feePercent ?? 0,
        selectedBillableActivities: {},
        currentTemplate: getInitialTemplateOption(
          templates,
          cp.contractTemplateId ?? 0,
        ),
        costShareRequired: !!cp.costShareOptions,
        deductionTiming: cp.costShareOptions?.deductionTiming ?? null,
        cadence: cp.costShareOptions?.cadence ?? null,
        billingTypeOverride: cp.billingTypeOverride ?? null,
        startDate: cp.startDate ? dayjs(cp.startDate).toDate() : null,
      }))
    : [
        {
          ...DEFAULT_PACKAGE_FORM_VALUES,
          price: 995, // Default price
          billingModelId: 0,
        },
      ],
});

const getInitialTemplateOption = (
  templates: ContractTemplateType[],
  contractTemplateId: number,
): ContractTemplateOption => {
  if (contractTemplateId === 0) {
    return NO_DATA_TEMPLATE;
  }
  const template = templates.find(
    template => template.id === contractTemplateId,
  );
  if (!template) {
    return NO_DATA_TEMPLATE;
  }
  return {
    name: template.name,
    contractTemplateId: template.id,
    contract: cleanTypeNameGraphql(template.contract),
  } as ContractTemplateOption;
};

export const getMostExpensiveBillingModelName = (
  contractPackages: ContractPackagesType[],
): string | null =>
  contractPackages.length > 0
    ? capitalizeFirstLetterAndSpaceSnakeString(
        contractPackages.reduce((maxCp, cp) =>
          !maxCp || cp.price > maxCp.price ? cp : maxCp,
        ).billingModel.name,
      )
    : null;

export const fieldHasErrors = (
  field: keyof RawContractPackageFormValues,
  errors?: FieldErrors<RawContractPackageFormValues>,
): boolean => !!errors?.[field] && Object.keys(errors[field]).length !== 0;

export const innerFieldHasErrors = (
  field: keyof RawInnerPackageFormValues,
  index: number,
  errors?: FieldErrors<RawContractPackageFormValues>,
): boolean => {
  const innerErrors = errors?.selectedPackages?.[
    index
  ] as FieldErrors<RawInnerPackageFormValues>;
  return !!innerErrors?.[field] && Object.keys(innerErrors[field]).length !== 0;
};
export const getFormErrorsForContractField = (
  field: keyof RawContractPackageFormValues,
  errors?: FieldErrors<RawContractPackageFormValues>,
): string => errors?.[field]?.message ?? '';

export const getFormErrorsForInnerField = (
  field: keyof RawInnerPackageFormValues,
  index: number,
  errors?: FieldErrors<RawContractPackageFormValues>,
): string => {
  const innerErrors = errors?.selectedPackages?.[
    index
  ] as FieldErrors<RawInnerPackageFormValues>;
  return innerErrors?.[field]?.message ?? '';
};
